import { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { formatCurrency, getDynamicTranslation } from '@monorepo/client-common';
import { AccountStatusTag, Flex } from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import {
  Account,
  AccountStatus,
  AccountType,
  DashboardLayouts,
  PayoutSource,
  Program,
} from '@monorepo/types';
import AccountCredentialsButton from '../account-credentials-button/account-credentials-button';
import ChoosePartnerButton from '../choose-partners-button/choose-partner-button';
import PageContainer from '../page-container/page-container';
import WithdrawFunds from '../withdraw-funds/withdraw-funds';

interface Props extends PropsWithChildren {
  account: Account;
  program?: Program;
  dashboard?: DashboardLayouts;
  showCredentials?: boolean;
  testId?: string;
  segments?: ReactNode;
}

const DashboardWrapper: FunctionComponent<Props> = ({
  account,
  children,
  program,
  dashboard,
  showCredentials = true,
  testId,
  segments,
}) => {
  const { isMobile } = useResponsiveness();
  const { t } = useTranslation(['dashboardTemplates']);

  const id = account.tsAccountId;
  const displayName = account.tsAccount || account.tsAccountId;
  const shouldShowPartnersButton =
    account &&
    (dashboard === DashboardLayouts.TTPDashboardNew ||
      dashboard === DashboardLayouts.TTPDashboard);
  const programName = program
    ? getDynamicTranslation('name', program, t) || program.name
    : undefined;

  const environmentName = programName;
  const environmentBaseBalance = program?.config.buyingPower;

  const pageTitle = `#${displayName} - ${environmentName} - ${formatCurrency(
    environmentBaseBalance || 0,
    {
      compact: true,
    },
  )}`;

  const isAllowedToWithdraw =
    account.type === AccountType.Funded &&
    account.status === AccountStatus.Active;

  const showCredentialsButton = showCredentials;

  return (
    <PageContainer
      testId={testId}
      segments={segments}
      headerContent={
        <Flex align="center" gap={16}>
          {isAllowedToWithdraw && (
            <WithdrawFunds
              sourceId={account._id}
              source={PayoutSource.Account}
              displayName={account.tsAccount || account.tsAccountId}
              tsAccountId={account.tsAccountId}
              account={'purchaseId' in account ? account : undefined}
            />
          )}
          {shouldShowPartnersButton && (
            <ChoosePartnerButton account={account as Account} />
          )}
          {showCredentialsButton && (
            <AccountCredentialsButton
              account={{
                tsUserId: account?.tsUserId || (account.tsAccountId as string),
                tsAccount: account.tsAccount || (account.tsAccountId as string),
                tsAccountId: account.tsAccountId as string,
                tradingSystem: account.tradingSystem,
              }}
            />
          )}
        </Flex>
      }
      pageTitlePostfix={
        <AccountStatusTag
          fill={false}
          status={account.status}
          state={account.state}
        />
      }
      className={classNames({
        'dashboard-page--mobile': isMobile,
      })}
      helmet={t('dashboardTemplates:account', { id })}
      pageTitle={pageTitle}
    >
      {children}
    </PageContainer>
  );
};
export default DashboardWrapper;
